<template>
	<div>

		<!-- ─── App Bar ─── -->
		<v-app-bar color="white" flat app elevation="1" height="62" class="app-bar">
			<v-btn icon @click="drawer = !drawer" class="toggle-btn">
				<v-icon color="#374151">mdi-menu</v-icon>
			</v-btn>

			<v-spacer></v-spacer>

			<div class="app-bar-right">
				<QrCodeVue userId="" />

				<!-- Cloche de notifications -->
				<v-menu v-model="bellMenu" offset-y left :nudge-bottom="8" :close-on-content-click="false" max-width="340">
					<template v-slot:activator="{ on, attrs }">
						<v-btn icon v-bind="attrs" v-on="on" class="bell-btn">
							<v-badge :value="badgeCount > 0" :content="badgeCount > 9 ? '9+' : String(badgeCount)" color="#c62828" overlap>
								<v-icon color="#374151">mdi-bell-outline</v-icon>
							</v-badge>
						</v-btn>
					</template>

					<v-card rounded="xl" class="bell-card" elevation="4">
						<!-- En-tête -->
						<div class="bell-header">
							<div class="bell-header-icon">
								<v-icon size="14" color="white">mdi-bell</v-icon>
							</div>
							<span class="bell-title">Rappels</span>
							<v-spacer></v-spacer>
							<span class="bell-count-chip">{{ bellReminders.length }} rappel{{ bellReminders.length !== 1 ? 's' : '' }}</span>
						</div>

						<v-divider></v-divider>

						<!-- Liste -->
						<div class="bell-list">
							<div v-if="bellLoading" class="bell-state">
								<v-progress-circular size="24" width="2" color="#c62828" indeterminate></v-progress-circular>
							</div>
							<div v-else-if="bellReminders.length === 0" class="bell-state">
								<v-icon size="34" color="#e5e7eb">mdi-bell-off-outline</v-icon>
								<p class="bell-empty-text">Aucun rappel</p>
							</div>
							<template v-else>
								<div
									v-for="r in bellRemindersFormatted"
									:key="r._id"
									class="bell-item"
								>
									<div class="bell-item-left">
										<div class="bell-item-dot" :style="{ background: r.dotColor }"></div>
									</div>
									<div class="bell-item-body">
										<div class="bell-item-top">
											<span class="bell-item-title">{{ r.title }}</span>
											<v-chip x-small :color="r.statusColor" :text-color="r.statusText" class="bell-chip">{{ r.statusLabel }}</v-chip>
										</div>
										<div class="bell-item-meta">
											<v-icon size="10" color="#9ca3af">mdi-calendar</v-icon>
											<span>{{ r.dateFormatted }}</span>
											<v-icon size="10" color="#9ca3af" class="ml-2">mdi-clock-outline</v-icon>
											<span>{{ r.time }}</span>
										</div>
										<p v-if="r.description" class="bell-item-desc">{{ r.description }}</p>
									</div>
								</div>
							</template>
						</div>

						<v-divider></v-divider>

						<!-- Pied de page -->
						<div class="bell-footer" @click="goToRappels">
							<v-icon size="13" color="#c62828" class="mr-1">mdi-arrow-right-circle</v-icon>
							<span>Gérer les rappels</span>
						</div>
					</v-card>
				</v-menu>

				<div class="app-bar-avatar">A</div>
			</div>
		</v-app-bar>

		<!-- ─── Navigation Drawer ─── -->
		<v-navigation-drawer
			v-model="drawer"
			app
			width="256"
			color="white"
			class="sidebar"
		>
			<!-- Barre accent top -->
			<div class="sb-accent"></div>

			<!-- Brand -->
			<div class="sb-brand">
				<img
					src="../assets/cez-logo.png"
					alt="CEZ Fitness"
					class="sb-logo"
				/>
				<p class="sb-brand-name">CEZ FITNESS</p>
				<p class="sb-brand-sub">{{ userName }}</p>
			</div>

			<!-- Navigation -->
			<nav class="sb-nav">

				<!-- Section : Principal -->
				<p class="sb-section-label">Principal</p>

				<div
					v-for="item in mainItems"
					:key="item.path"
					class="sb-item"
					:class="{ 'sb-active': isActive(item.path) }"
					@click="navigate(item.path)"
				>
					<div class="sb-item-icon">
						<v-icon size="17" :color="isActive(item.path) ? 'white' : '#6b7280'">
							{{ item.icon }}
						</v-icon>
					</div>
					<span class="sb-item-label">{{ item.title }}</span>
					<div v-if="isActive(item.path)" class="sb-active-dot"></div>
				</div>

				<!-- Section : Programmes -->
				<p class="sb-section-label">Programmes</p>

				<!-- Protocoles (expandable) -->
				<div
					class="sb-item sb-expandable"
					:class="{ 'sb-prefix-active': isPrefix('/protocoles') }"
					@click="expandProtocoles = !expandProtocoles"
				>
					<div class="sb-item-icon">
						<v-icon size="17" :color="isPrefix('/protocoles') ? '#ef5350' : '#6b7280'">
							mdi-dumbbell
						</v-icon>
					</div>
					<span class="sb-item-label">Protocoles</span>
					<v-icon
						size="14"
						class="sb-chevron"
						:color="expandProtocoles ? '#ef5350' : '#4b5563'"
					>
						{{ expandProtocoles ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
					</v-icon>
				</div>
				<transition name="sb-slide">
					<div v-if="expandProtocoles" class="sb-sub">
						<div
							v-for="sub in protocols"
							:key="sub.path"
							class="sb-sub-item"
							:class="{ 'sb-sub-active': isActive(sub.path) }"
							@click="navigate(sub.path)"
						>
							<span class="sb-sub-line"></span>
							{{ sub.title }}
						</div>
					</div>
				</transition>

				<!-- Repas (expandable) -->
				<div
					class="sb-item sb-expandable"
					:class="{ 'sb-prefix-active': isPrefix('/repas') }"
					@click="expandRepas = !expandRepas"
				>
					<div class="sb-item-icon">
						<v-icon size="17" :color="isPrefix('/repas') ? '#ef5350' : '#6b7280'">
							mdi-food
						</v-icon>
					</div>
					<span class="sb-item-label">Repas</span>
					<v-icon
						size="14"
						class="sb-chevron"
						:color="expandRepas ? '#ef5350' : '#4b5563'"
					>
						{{ expandRepas ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
					</v-icon>
				</div>
				<transition name="sb-slide">
					<div v-if="expandRepas" class="sb-sub">
						<div
							v-for="sub in mealsMenus"
							:key="sub.path"
							class="sb-sub-item"
							:class="{ 'sb-sub-active': isActive(sub.path) }"
							@click="navigate(sub.path)"
						>
							<span class="sb-sub-line"></span>
							{{ sub.title }}
						</div>
					</div>
				</transition>

				<!-- Séances (expandable) -->
				<div
					class="sb-item sb-expandable"
					:class="{ 'sb-prefix-active': isPrefix('/seances') }"
					@click="expandSeances = !expandSeances"
				>
					<div class="sb-item-icon">
						<v-icon size="17" :color="isPrefix('/seances') ? '#ef5350' : '#6b7280'">
							mdi-calendar-clock
						</v-icon>
					</div>
					<span class="sb-item-label">Séances</span>
					<v-icon
						size="14"
						class="sb-chevron"
						:color="expandSeances ? '#ef5350' : '#4b5563'"
					>
						{{ expandSeances ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
					</v-icon>
				</div>
				<transition name="sb-slide">
					<div v-if="expandSeances" class="sb-sub">
						<div
							v-for="sub in seancesMenus"
							:key="sub.path"
							class="sb-sub-item"
							:class="{ 'sb-sub-active': isActive(sub.path) }"
							@click="navigate(sub.path)"
						>
							<span class="sb-sub-line"></span>
							{{ sub.title }}
						</div>
					</div>
				</transition>

				<!-- Section : Autre -->
				<p class="sb-section-label">Autre</p>

				<div
					v-for="item in otherItems"
					:key="item.path"
					class="sb-item"
					:class="{ 'sb-active': isActive(item.path) }"
					@click="navigate(item.path)"
				>
					<div class="sb-item-icon">
						<v-icon size="17" :color="isActive(item.path) ? 'white' : '#6b7280'">
							{{ item.icon }}
						</v-icon>
					</div>
					<span class="sb-item-label">{{ item.title }}</span>
					<div v-if="isActive(item.path)" class="sb-active-dot"></div>
				</div>

				<!-- Section : Gestion -->
				<p class="sb-section-label">Gestion</p>

				<div
					v-for="item in gestionItems"
					:key="item.path"
					class="sb-item"
					:class="{ 'sb-active': isActive(item.path) }"
					@click="navigate(item.path)"
				>
					<div class="sb-item-icon">
						<v-icon size="17" :color="isActive(item.path) ? 'white' : '#6b7280'">
							{{ item.icon }}
						</v-icon>
					</div>
					<span class="sb-item-label">{{ item.title }}</span>
					<div v-if="isActive(item.path)" class="sb-active-dot"></div>
				</div>

			</nav>

			<!-- Footer utilisateur -->
			<template v-slot:append>
				<div class="sb-footer">
					<div class="sb-footer-sep"></div>
					<div class="sb-user">
						<div class="sb-user-avatar">{{ userInitial }}</div>
						<div class="sb-user-info">
							<p class="sb-user-name">{{ userName }}</p>
							<p class="sb-user-role">{{ userRole }}</p>
					</div>
					<v-tooltip top>
						<template v-slot:activator="{ on, attrs }">
							<v-btn icon x-small class="sb-logout-btn" v-bind="attrs" v-on="on" @click="logout">
								<v-icon size="16" color="#4b5563">mdi-logout</v-icon>
							</v-btn>
						</template>
						<span>Se déconnecter</span>
					</v-tooltip>
				</div>
			</div>
		</template>

		</v-navigation-drawer>
	</div>
</template>

<script>
import QrCodeVue from "@/components/Admin/Dashboard/QrCode.vue";
import service from "../services";
export default {
	components: { QrCodeVue },

	data() {
		return {
			drawer: null,
			expandProtocoles: false,
			expandRepas: false,

			bellMenu: false,
			bellReminders: [],
			bellLoading: false,

			mainItems: [
				{ title: "Tableau de bord", icon: "mdi-view-dashboard",  path: "/admin"       },
				{ title: "Clients",          icon: "mdi-account-group",   path: "/clients"     },
				{ title: "Abonnements",      icon: "mdi-playlist-check",  path: "/abonnements" },
				{ title: "Parrainages",      icon: "mdi-family-tree",     path: "/parrainages" },
				{ title: "Coachs",           icon: "mdi-account-tie",     path: "/coachs"      },
			],

			protocols: [
				{ title: "Liste",     path: "/protocoles/liste"    },
				{ title: "Exercices", path: "/protocoles/exercices" },
			],

			mealsMenus: [
				{ title: "Liste",      path: "/repas/liste"      },
				{ title: "Catégories", path: "/repas/categories" },
			],

			seancesMenus: [
				{ title: "Liste",        path: "/seances/liste"        },
				{ title: "Attributions", path: "/seances/attributions" },
			],
			expandSeances: false,

			otherItems: [
				// { title: "Transactions", icon: "mdi-cash",                      path: "/transactions" },
				{ title: "FAQ",          icon: "mdi-frequently-asked-questions", path: "/faq"          },
			],

			gestionItems: [
				{ title: "Dépenses",     icon: "mdi-cash-minus",                path: "/depenses"     },
				{ title: "Stock",        icon: "mdi-package-variant-closed",    path: "/stock"        },
				{ title: "Fournisseurs", icon: "mdi-truck-delivery-outline",    path: "/fournisseurs" },
				{ title: "Chat",         icon: "mdi-chat-outline",              path: "/chat"         },
				{ title: "Rappels",      icon: "mdi-bell-outline",              path: "/rappels"      },
				{ title: "Salles",       icon: "mdi-domain",                    path: "/salles"       },
			],
		};
	},

	computed: {
		currentUser() { return this.$store.getters.currentUser; },
		userName() { return this.currentUser?.id?.fullName || 'Administrateur'; },
		userRole() { return this.currentUser?.type || 'CEZ Fitness Club'; },
		userInitial() { return (this.currentUser?.id?.fullName || 'A').charAt(0).toUpperCase(); },
		badgeCount() {
			const today = new Date().toISOString().split('T')[0];
			return this.bellReminders.filter(r => {
				const d = (r.date || '').split('T')[0];
				return d <= today;
			}).length;
		},
		bellRemindersFormatted() {
			const today = new Date().toISOString().split('T')[0];
			return this.bellReminders.map(r => {
				const d = (r.date || '').split('T')[0];
				let statusLabel = 'À venir';
				let statusColor = '#eff6ff';
				let statusText = '#1565c0';
				let dotColor = '#93c5fd';
				if (d < today) {
					statusLabel = 'En retard'; statusColor = '#fee2e2'; statusText = '#991b1b'; dotColor = '#f87171';
				} else if (d === today) {
					statusLabel = "Aujourd'hui"; statusColor = '#fff7ed'; statusText = '#9a3412'; dotColor = '#fb923c';
				}
				return {
					...r,
					dateFormatted: d ? new Intl.DateTimeFormat('fr-FR').format(new Date(d)) : '—',
					statusLabel, statusColor, statusText, dotColor,
				};
			});
		},
	},

	watch: {
		$route(to) {
			if (to.path.startsWith("/protocoles")) this.expandProtocoles = true;
			if (to.path.startsWith("/repas"))      this.expandRepas      = true;
			if (to.path.startsWith("/seances"))    this.expandSeances    = true;
		},
		bellMenu(val) {
			if (val) this.loadBellReminders();
		},
	},

	created() {
		if (this.$route.path.startsWith("/protocoles")) this.expandProtocoles = true;
		if (this.$route.path.startsWith("/repas"))      this.expandRepas      = true;
		if (this.$route.path.startsWith("/seances"))    this.expandSeances    = true;
		this.loadBellReminders();
	},

	methods: {
		navigate(path) {
			if (this.$route.path !== path) this.$router.push(path);
		},
		isActive(path) {
			return this.$route.path === path;
		},
		isPrefix(prefix) {
			return this.$route.path.startsWith(prefix);
		},
		loadBellReminders() {
			this.bellLoading = true;
			service.getReminders({ page: 1, limit: 20 })
				.then(r => { this.bellReminders = r.data?.rappels || []; })
				.catch(() => {})
				.finally(() => { this.bellLoading = false; });
		},
		goToRappels() {
			this.bellMenu = false;
			if (this.$route.path !== '/rappels') this.$router.push('/rappels');
		},
		logout() {
			this.$store.dispatch('logout');
			this.$router.replace('/');
		},
	},
};
</script>

<style scoped>
/* ─── App Bar ─── */

.app-bar {
	border-bottom: 1px solid #f3f4f6 !important;
}

.toggle-btn {
	margin-left: 2px;
}

.app-bar-right {
	display: flex;
	align-items: center;
	gap: 4px;
	padding-right: 4px;
}

.app-bar-avatar {
	width: 34px;
	height: 34px;
	border-radius: 50%;
	background: linear-gradient(135deg, #c62828, #1565c0);
	color: white;
	font-size: 0.72rem;
	font-weight: 800;
	display: flex;
	align-items: center;
	justify-content: center;
	letter-spacing: 0.5px;
	margin-left: 4px;
}

/* ─── Bell ─── */

.bell-btn {
	position: relative;
}

.bell-card {
	font-family: Montserrat, sans-serif;
	overflow: hidden;
	border-radius: 16px !important;
}

.bell-header {
	display: flex;
	align-items: center;
	gap: 10px;
	padding: 14px 16px 12px;
}

.bell-header-icon {
	width: 28px;
	height: 28px;
	border-radius: 8px;
	background: #c62828;
	display: flex;
	align-items: center;
	justify-content: center;
	flex-shrink: 0;
}

.bell-title {
	font-size: 0.86rem;
	font-weight: 700;
	color: #111827;
}

.bell-count-chip {
	font-size: 0.68rem;
	font-weight: 600;
	color: #6b7280;
	background: #f3f4f6;
	border-radius: 20px;
	padding: 2px 8px;
}

.bell-list {
	max-height: 320px;
	overflow-y: auto;
}

.bell-state {
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	gap: 8px;
	padding: 28px 16px;
}

.bell-empty-text {
	font-size: 0.78rem;
	color: #9ca3af;
	margin: 0;
	font-family: Montserrat, sans-serif;
}

.bell-item {
	display: flex;
	gap: 10px;
	padding: 11px 16px;
	border-bottom: 1px solid #f9fafb;
	transition: background 0.12s;
}

.bell-item:last-child {
	border-bottom: none;
}

.bell-item:hover {
	background: #fafafa;
}

.bell-item-left {
	display: flex;
	flex-direction: column;
	align-items: center;
	padding-top: 5px;
}

.bell-item-dot {
	width: 7px;
	height: 7px;
	border-radius: 50%;
	flex-shrink: 0;
}

.bell-item-body {
	flex: 1;
	min-width: 0;
}

.bell-item-top {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 6px;
	margin-bottom: 3px;
}

.bell-item-title {
	font-size: 0.8rem;
	font-weight: 600;
	color: #111827;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
	flex: 1;
}

.bell-chip {
	flex-shrink: 0;
	font-family: Montserrat, sans-serif !important;
	font-weight: 600 !important;
}

.bell-item-meta {
	display: flex;
	align-items: center;
	gap: 3px;
	font-size: 0.68rem;
	color: #9ca3af;
	font-family: Montserrat, sans-serif;
}

.bell-item-desc {
	font-size: 0.7rem;
	color: #6b7280;
	margin: 4px 0 0;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
}

.bell-footer {
	display: flex;
	align-items: center;
	padding: 11px 16px;
	cursor: pointer;
	font-size: 0.76rem;
	font-weight: 600;
	color: #c62828;
	transition: background 0.12s;
	font-family: Montserrat, sans-serif;
}

.bell-footer:hover {
	background: #fff5f5;
}

/* ─── Sidebar ─── */

.sidebar {
	font-family: Montserrat, sans-serif;
	overflow: hidden;
}

/* Barre accent top */
.sb-accent {
	height: 3px;
	background: linear-gradient(to right, #c62828, #1565c0);
}

/* Brand */
.sb-brand {
	padding: 28px 24px 20px;
	border-bottom: 1px solid #f3f4f6;
}

.sb-logo {
	width: 52px;
	display: block;
	margin-bottom: 14px;
}

.sb-brand-name {
	font-size: 0.85rem;
	font-weight: 800;
	color: #111827;
	letter-spacing: 2px;
	margin: 0 0 3px;
}

.sb-brand-sub {
	font-size: 0.72rem;
	font-weight: 600;
	color: #c62828;
	letter-spacing: 0.3px;
	margin: 0;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
}

/* Navigation */
.sb-nav {
	padding: 12px 12px 0;
	overflow-y: auto;
}

/* Section label */
.sb-section-label {
	font-size: 0.58rem;
	font-weight: 700;
	color: #4b5563;
	letter-spacing: 2px;
	text-transform: uppercase;
	padding: 14px 10px 6px;
	margin: 0;
}

/* Nav item */
.sb-item {
	display: flex;
	align-items: center;
	gap: 12px;
	padding: 10px 10px;
	border-radius: 10px;
	cursor: pointer;
	transition: background 0.15s ease;
	margin-bottom: 2px;
	position: relative;
}

.sb-item:hover {
	background: #f3f4f6;
}

.sb-item-icon {
	width: 32px;
	height: 32px;
	border-radius: 8px;
	display: flex;
	align-items: center;
	justify-content: center;
	background: rgba(0, 0, 0, 0.04);
	flex-shrink: 0;
}

.sb-item-label {
	font-size: 0.82rem;
	font-weight: 500;
	color: #374151;
	flex: 1;
	transition: color 0.15s;
}

/* Item actif */
.sb-active {
	background: rgba(198, 40, 40, 0.15) !important;
}

.sb-active .sb-item-icon {
	background: #c62828;
}

.sb-active .sb-item-label {
	color: #c62828;
	font-weight: 600;
}

.sb-active-dot {
	width: 5px;
	height: 5px;
	border-radius: 50%;
	background: #c62828;
	flex-shrink: 0;
}

/* Expandable */
.sb-expandable {
	user-select: none;
}

.sb-chevron {
	flex-shrink: 0;
}

/* Prefix active (section active) */
.sb-prefix-active .sb-item-label {
	color: #ef5350;
	font-weight: 600;
}

/* Sub items */
.sb-sub {
	padding-left: 22px;
	overflow: hidden;
}

.sb-sub-item {
	display: flex;
	align-items: center;
	gap: 10px;
	padding: 8px 10px;
	border-radius: 8px;
	cursor: pointer;
	font-size: 0.78rem;
	font-weight: 500;
	color: #6b7280;
	transition: all 0.15s;
	margin-bottom: 1px;
}

.sb-sub-item:hover {
	background: #f3f4f6;
	color: #111827;
}

.sb-sub-line {
	width: 14px;
	height: 1px;
	background: #e5e7eb;
	flex-shrink: 0;
	display: block;
	transition: background 0.15s;
}

.sb-sub-active {
	color: #ef5350;
	font-weight: 600;
}

.sb-sub-active .sb-sub-line {
	background: #ef5350;
}

/* Transition slide */
.sb-slide-enter-active,
.sb-slide-leave-active {
	transition: all 0.22s ease;
	overflow: hidden;
	max-height: 200px;
}

.sb-slide-enter,
.sb-slide-leave-to {
	max-height: 0;
	opacity: 0;
}

/* ─── Footer utilisateur ─── */

.sb-footer {
	padding: 0 12px 12px;
}

.sb-footer-sep {
	height: 1px;
	background: #f3f4f6;
	margin-bottom: 14px;
}

.sb-user {
	display: flex;
	align-items: center;
	gap: 10px;
	padding: 10px;
	border-radius: 12px;
	background: #f9fafb;
}

.sb-user-avatar {
	width: 34px;
	height: 34px;
	border-radius: 50%;
	background: linear-gradient(135deg, #c62828, #1565c0);
	color: white;
	font-size: 0.72rem;
	font-weight: 800;
	display: flex;
	align-items: center;
	justify-content: center;
	flex-shrink: 0;
}

.sb-user-info {
	flex: 1;
	min-width: 0;
}

.sb-user-name {
	font-size: 0.78rem;
	font-weight: 600;
	color: #111827;
	margin: 0 0 1px;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
}

.sb-user-role {
	font-size: 0.65rem;
	color: #6b7280;
	margin: 0;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
}

.sb-logout-btn {
	flex-shrink: 0;
}
</style>
